import { Component, Input, OnInit, ChangeDetectorRef } from '@angular/core';
import { BaseComponent } from '@lib/components';
import { TColumn, TColumnSort } from '@lib/modules/table/components';
import { ApiResponseError, ISearchBooleanQueryMust, ISearchBooleanQueryMustBoolShould, ISearchWalletResponse, IWalletIndex, Nullable, WalletTypes } from '@lib/interfaces';
import { Permission, WalletType } from '@lib/enums';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CardManagementService } from '@lib/services';
import { NgOptimizedImage } from '@angular/common';
import { TableModule } from '@lib/modules';
import { AngularMaterialModule } from '../../../../material.module';
import { MessageTypePipe, OptionalPlaceholderPipe, SourceChannelPipe, TransactionStatusPipe, TransactionTypePipe } from '@lib/pipes';
import { finalize } from 'rxjs';
import { TransactionIconComponent } from '@lib/svg';
import { ICursorForm, ISearchBooleanQueryForm, ISearchBooleanQueryMustFormArray, ISearchCashbackWalletRequestForm, TransactionSearchService } from '@pages/transaction/services';
import { WalletTypePipe } from '../../../pipes/wallet-type/wallet-type.pipe';
import { Router } from '@angular/router';
import { Utils } from '@lib/utils';
import { customerTenant } from '@env/environment';
import { TenantService } from '@lib/services/tenant.service';
import { Store } from '@ngrx/store';
import { AppState } from '@store/app.state';
import { selectTableColumnState } from '@store/selectors/column-state.selectors';
import { setTableColumnState } from '@store/actions/column-state.actions';

@Component({
    standalone: true,
    selector: 'app-wallet',
    templateUrl: './wallet.component.html',
    styleUrls: ['./wallet.component.scss'],
    imports: [AngularMaterialModule, TableModule, TransactionStatusPipe, OptionalPlaceholderPipe, NgOptimizedImage, TransactionTypePipe, TransactionIconComponent, MessageTypePipe, SourceChannelPipe, WalletTypePipe],
})
export class WalletComponent extends BaseComponent implements OnInit {
    @Input() public filters: Array<ISearchBooleanQueryMust> = [];
    @Input() public isOpenedFromCardOverview = true;
    @Input() public transactionWalletTypes: Array<WalletType> = Object.values(WalletType);
    @Input() public defaultWalletTypes: Array<ISearchBooleanQueryMustBoolShould> = WalletTypes;

    protected columns: Array<TColumn> = [
        { fee_amount: 'Fee Amount' },
        { from_amount: 'From Amount' },
        { from_currency: 'From Currency' },
        { id: 'ID' },
        { notes: 'Notes' },
        { recipient_account_id: 'Recipient Account ID' },
        { recipient_alias: 'Recipient Alias' },
        { recipient_mobile: 'Recipient Mobile' },
        { recipient_name: 'Recipient Name' },
        { recipient_user_id: 'Recipient User ID' },
        { merchant_name: 'Merchant Name' },
        { rrn: 'RRN' },
        { sender_account_id: 'Sender Account ID' },
        { sender_card_last4: 'Sender Card Last Four' },
        { sender_name: 'Sender Name' },
        { sender_user_id: 'Sender User ID' },
        { status_code: 'Status' },
        { to_amount: 'To Amount' },
        { to_currency: 'To Currency' },
        { transaction_timestamp: 'Date & Time' },
        { type: 'Type' },
        { message_type: 'Message Type' },
    ];
    protected defaultColumns: Array<string> = ['transaction_timestamp', 'type', 'recipient_name', 'sender_name', 'to_amount', 'notes', 'status_code'];
    protected tenantDefaultColumns: Array<string> = ['transaction_timestamp', 'type', 'recipient_name', 'sender_name', 'to_amount', 'notes', 'status_code'];
    protected sortableColumns: Array<string> = [];
    protected wallets: Array<IWalletIndex> = [];
    protected hasMoreRecords = true;
    protected loading = false;
    protected walletHeading: string;
    private apiInProgress = false;
    private searchPayload: FormGroup<ISearchCashbackWalletRequestForm> = this.createSearchWalletFormGroup();
    protected currentTenant: string;
    protected noDataMessage: string;
    protected customerTenant = customerTenant;
    protected visibleColumns: string[] = [];
    private readonly tableId = 'TransactionWalletTable';

    get filterName(): string {
        const tenantPrefix = this.currentTenant ? this.currentTenant.split('_')[0] : '';
        return this.customerTenant?.INC === tenantPrefix ? 'Transfers Type' : 'Wallet Type';
    }

    public constructor(
        private readonly utils: Utils,
        private readonly router: Router,
        private readonly formBuilder: FormBuilder,
        private readonly _snackBar: MatSnackBar,
        private readonly cardManagementService: CardManagementService,
        private readonly transactionSearchService: TransactionSearchService,
        private readonly tenantService: TenantService,
        private readonly store: Store<AppState>,
        private readonly changeDetectorRef: ChangeDetectorRef,
    ) {
        super();
        this.currentTenant = this.tenantService.getTenantKey() || '';
        this.noDataMessage = this.customerTenant.INC === this.currentTenant.split('_')[0] ? 'No transfers found' : 'Unable to load transactions';
    }

    public ngOnInit(): void {
        this.walletHeading = customerTenant.INC === this.currentTenant.split('_')[0] ? 'Transfers' : `Wallet`;
        if (customerTenant.BAYBLOS === this.currentTenant) {
            this.defaultColumns = ['transaction_timestamp', 'type', 'recipient_name', 'sender_name', 'to_amount', 'from_amount', 'notes', 'status_code'];
            this.tenantDefaultColumns = this.defaultColumns;
        }
        this.applyBuiltInFilters();
        this.reFetchWallets();
        this.initializeDefaultColumns();
    }

    protected searchWallets(): void {
        if (this.apiInProgress) return;

        this.apiInProgress = true;

        this.addSubscription(
            this.cardManagementService
                .searchWallet(this.searchPayload.value)
                .pipe(
                    finalize((): void => {
                        this.loading = false;
                        this.apiInProgress = false;
                    }),
                )
                .subscribe({
                    next: ({ data, paging }: ISearchWalletResponse): void => {
                        if (!this.isOpenedFromCardOverview) {
                            this.wallets = data.slice(0, 5);
                            this.hasMoreRecords = false;
                        } else {
                            this.wallets = [...this.wallets, ...data];

                            if (!paging || paging.cursors?.has_more === false) {
                                this.hasMoreRecords = false;

                                return;
                            }
                            this.searchPayload.controls.cursors?.controls.after?.setValue(paging.cursors.after ?? '');
                        }
                    },
                    error: (error: ApiResponseError): void => {
                        this.hasMoreRecords = false;
                        if (this.customerTenant.BAYBLOS === this.currentTenant) this._snackBar.open('Unable to load transactions', 'Close', { duration: 6000 });
                        else this.createApiErrorSnackBar(this._snackBar, error);
                    },
                }),
        );
    }

    protected getTransactionTypeIcon(type: string, transaction: IWalletIndex): string {
        if (type == WalletType.QR_PAYMENTS || type == WalletType.QR_PAYMENTS_REFUND || type == WalletType.QR_PAYMENTS_VOID) {
            return transaction.merchant_name ? 'from ' + transaction.merchant_name : 'from Unknown Merchant';
        }
        if (type == WalletType.NCPGS_TRANSFER) {
            return transaction.sender_card_last4 ? '**' + transaction.sender_card_last4 : '';
        }
        return '';
    }

    protected applySort(sortBy: Nullable<TColumnSort>): void {
        this.clearWallets();
        if (sortBy) {
            this.searchPayload.controls.sort_by?.setValue(sortBy.key);
            this.searchPayload.controls.order_by?.setValue(sortBy.sort);
        }
    }

    private reFetchWallets(): void {
        this.loading = true;
        this.hasMoreRecords = true;

        this.searchWallets();
    }

    private clearWallets(): void {
        this.searchPayload.controls.cursors?.setValue({
            after: '',
        });
        this.wallets = [];
    }

    private createSearchWalletFormGroup(): FormGroup<ISearchCashbackWalletRequestForm> {
        return this.formBuilder.group<ISearchCashbackWalletRequestForm>({
            cursors: this.formBuilder.group<ICursorForm>({
                after: this.formBuilder.nonNullable.control<string>(''),
            }),
            limit: this.formBuilder.nonNullable.control<number>(10),
            sort_by: this.formBuilder.nonNullable.control<string>('transaction_timestamp'),
            order_by: this.formBuilder.nonNullable.control<string>('desc'),
            boolean_query: this.formBuilder.group<ISearchBooleanQueryForm>({
                must: this.formBuilder.array<ISearchBooleanQueryMustFormArray>([]),
            }),
        });
    }

    private applyBuiltInFilters(): void {
        this.filters.forEach((filter: ISearchBooleanQueryMust) => {
            this.transactionSearchService.addCashbackWalletFilter(this.searchPayload, filter);
        });
    }

    protected applyProductTypeFilter(walletTypes: Array<string>): void {
        this.clearWallets();
        this.searchPayload.controls.boolean_query?.controls.must?.clear();
        const index = this.filters.findIndex((filter) => filter.bool.should?.find((shouldFilter) => shouldFilter.fieldName == 'type'));
        const typesArray: Array<ISearchBooleanQueryMustBoolShould> = [];
        walletTypes.forEach((type: string) => {
            const typeObj: ISearchBooleanQueryMustBoolShould = {
                queryObjectType: 'match',
                fieldName: 'type',
                fieldValue: type,
            };
            if (type === WalletType.QR_PAYMENTS_REFUND) {
                typesArray.push({
                    queryObjectType: 'match',
                    fieldName: 'type',
                    fieldValue: WalletType.QR_PAYMENTS_VOID,
                });
            }
            typesArray.push(typeObj);
        });
        this.filters[index].bool.should = typesArray.length == 0 ? this.defaultWalletTypes : typesArray;
        this.applyBuiltInFilters();
        this.reFetchWallets();
    }
    protected async goToTransactionDetail(transaction: IWalletIndex): Promise<void> {
        const canViewDetails: boolean = this.utils.getPermissions(Permission.TransactionDetails);

        if (!canViewDetails) {
            this._snackBar.open('You do not have permission to view transaction details', 'Close', { duration: 6000 });
            return;
        }

        await this.router.navigate(['transactions', transaction.id]);
    }
    protected exportExcel(): void {
        const snackBarRef = this._snackBar.open('Download in Progress', 'Close', { duration: 7000 });
        let selected_columns: string[] = this.visibleColumns.length > 0 ? this.visibleColumns : this.defaultColumns;
        selected_columns = selected_columns.map((column) => (column === 'card_last4_digits' ? 'cardNumber' : column));
        const { limit, cursors, ...filters } = this.searchPayload.value || {};
        this.addSubscription(
            this.cardManagementService.exportTransactionExcelTable('ACCOUNT', filters, selected_columns).subscribe({
                next: (res) => {
                    snackBarRef.dismiss();
                    this.utils.downloadFile(res);
                },
                error: (error: ApiResponseError) => {
                    this.createApiErrorSnackBar(this._snackBar, error);
                },
            }),
        );
    }

    private initializeDefaultColumns(): void {
        if (this.tableId) {
            this.addSubscription(
                this.store.select(selectTableColumnState(this.tableId)).subscribe((tableColumns) => {
                    if (tableColumns?.length) {
                        this.defaultColumns = [...tableColumns];
                    } else {
                        this.defaultColumns = [...this.tenantDefaultColumns];
                    }
                    this.changeDetectorRef.detectChanges();
                }),
            );
        } else {
            this.defaultColumns = [...this.tenantDefaultColumns];
        }
    }

    protected handleVisibleColumnsChange(columns: string[]): void {
        if (this.tableId) {
            this.store.dispatch(
                setTableColumnState({
                    tableId: this.tableId,
                    columns,
                }),
            );
        }
        this.changeDetectorRef.detectChanges();
    }
}
